import {
  AgentSessionStatus,
  AgentSessionStepType,
  AgentSessionType,
  CustomGapAgentSession,
} from "@/modules/sessions/types";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { Spinner } from "@nextui-org/react";
import { Eye } from "lucide-react";
import { ReactNode, Suspense, useEffect, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { cn } from "@/shared/utils/classname-merger";
import { helper } from "../../constants/helpers";
import {
  getAgentTypeFromId,
  useAgentSessionData,
  useAgentStepData,
} from "../../states";
import { AGENT_TYPES, GapAssessmentTypes, RiskAssessmentTypes } from "../../types/index.ts";

import AgentHeader from "./header";
import { ReopenedSessionBanner } from "./ReopenedSessionBanner";
import { useBannerLayoutStore } from "../../states/banner-layout";
// const Intermediate = lazy(() => import("../intermediate"));
import AskAi from "@/modules/assistant/components/index.tsx";
import { useChatStore } from "@/modules/assistant/states/index.ts";
import CustomizeControls from "../customize-controls/CustomizeControls.tsx";
import Intermediate from "../intermediate/index.tsx";
import QuestionnaireMapping from "../questionnaire-mapping/index.tsx";
import ReviewResponse from "../review-responses/index.tsx";
import VendorRiskAssessment from "../VendorRiskAssessment/VendorRiskAssessment.tsx";
import Editor from "../Editor/index.tsx";
import AgentWaiting from "../VendorEmail/AgentWaiting.tsx";
import { useCustomizeControlsStore, getCustomizeControlsActions } from "../../states/cusomize-controls.ts";
import { useVendorRiskAssessmentStore } from "../VendorRiskAssessment/useVendorRiskAssessment/useVendorRiskAssessmentStore.ts";
import { useGetFrameworksListQuery } from "../../requests/index.ts";
import AssessorSideIntakeForm from "../VendorAssessmentIntakeForm/AssessorSideIntakeForm.tsx";
import TieringStep from "../VendorAssessmentIntakeForm/TieringStep.tsx";
import LaunchStep from "../VendorAssessmentIntakeForm/LaunchStep.tsx";

interface Params {
  id: string;
}

const ChatLayout = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("mode") === "view";

  const stepData = useAgentStepData(id);

  const {isFloatingAskAiOpen, setIsFloatingAskAiOpen} = useChatStore();
  const { setShouldRemovePadding } = useBannerLayoutStore();

  const sessionData = useAgentSessionData(id);

  const workFlowName = useMemo(() => {
    if (!sessionData?.name) {
      return "Review Responses";
    }
    return sessionData?.name;
  }, [sessionData?.name]);

  const { isCustomControls } = useCustomizeControlsStore();

  const { data: customFrameworksList } = useGetFrameworksListQuery({ isEnabled : sessionData && sessionData?.type === AgentSessionType.GAP_CUSTOM_FRAMEWORK })

  const {
    title,
    breadcrumb,
    currentComponent,
    isIntermediate,
  } = useMemo(() => {
    const { agentType: task, agentSubType } = getAgentTypeFromId(id);
    if (!task || !stepData)
      return {
        title: "",
        breadcrumb: "Review Responses",
        currentComponent: null,
        isIntermediate: false,
      };

    const data = JSON.parse(JSON.stringify(helper[agentSubType]));    
    if (!data)
      return {
        title: "",
        breadcrumb: "Review Responses",
        currentComponent: null,
        isIntermediate: false,
      };
     

    const inProgressStep = stepData.find(
      (step) =>
        step.status === AgentSessionStatus.IN_PROGRESS ||
        step.status === AgentSessionStatus.INPUT_NEEDED ||
        step.status === AgentSessionStatus.UNDER_REASSESSMENT || 
        step.status === AgentSessionStatus.VENDOR_INPUT_NEEDED
    );
  

    const prepareReportStep = stepData.find(
      (step) => step.type === AgentSessionStepType.PREPARE_REPORT
    );

    let component = null;
    let isIntermediate = false;

    if (sessionData?.status === AgentSessionStatus.FORM_SENT) {
      component = <AgentWaiting />
      isIntermediate = true;
    }

    else if (
      inProgressStep?.type &&
      [
        AgentSessionStepType.POLICY_MANAGEMENT,
        AgentSessionStepType.GENERATE_RISK_ASSESSMENT_RESPONSE,
        AgentSessionStepType.GENERATE_FAIR_ASSESSMENT_RESPONSE,
        AgentSessionStepType.QA_EXCEL_STRUCTURE_MAPPING,
        AgentSessionStepType.QA_FILLER,
        AgentSessionStepType.SOC2_TYPE2_AUDIT,
        AgentSessionStepType.SOC2_TYPE2,
        AgentSessionStepType.SOC2_TYPE1,
        AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT,
        AgentSessionStepType.DORA_ASSESSMENT,
        AgentSessionStepType.NIST_AI_RMF,
        AgentSessionStepType.GAP_CUSTOM_FRAMEWORK,
        AgentSessionStepType.LOAD_TEMPLATE
      ].includes(inProgressStep.type)
    ) {
      component = <Intermediate />;
      isIntermediate = true;
    } else if (
      inProgressStep?.type &&
      [AgentSessionStepType.MULTI_VENDOR_PROFILE].includes(inProgressStep.type)
    ) {
      const baseTitle = helper[agentSubType].title;
      const vendorName = (inProgressStep?.data as any)?.vendor_intake_form?.vendor_name || "";
      const productName = (inProgressStep?.data as any)?.vendor_intake_form?.product_name || "";

      data.title = `${vendorName} ${productName} ${baseTitle}`.trim();
      
      // Vendor intake form
      const controlSelection = stepData.find(
        (step) =>
          step.type === AgentSessionStepType.CONTROL_SELECTION
      );
      
      // Check if create_new_control_set is enabled
      const isCreateNewControlSet = (sessionData as any)?.multi_file_vendor_assessment?.create_new_control_set === true;
      
      if (stepData[0].type === AgentSessionStepType.MULTI_VENDOR_PROFILE && (stepData[0].status === AgentSessionStatus.INPUT_NEEDED || stepData[0].status === AgentSessionStatus.IN_PROGRESS)) {
        const intakeFormVersion = (sessionData as any)?.multi_file_vendor_assessment?.intake_form_version;

        if (intakeFormVersion === 1) {
          // v1 sessions: AssessorSideIntakeForm only, no stepper progression
          component = <AssessorSideIntakeForm />;
        } else {
          // v2+ sessions: full stepper flow
          const reviewStep = searchParams.get("reviewStep") || "1";
          if (reviewStep === "2") {
            component = <TieringStep />;
          } else if (reviewStep === "3") {
            component = <LaunchStep />;
          } else {
            component = <AssessorSideIntakeForm />;
          }
        }
       }
      // FIXME change needed
      // Only show CustomizeControls if not create_new_control_set OR if we haven't sent the form yet
      else if (
        ((stepData[0].status === AgentSessionStatus.VENDOR_INPUT_NEEDED) || controlSelection?.status === AgentSessionStatus.INPUT_NEEDED) &&
        (!isCreateNewControlSet || (sessionData as any)?.status !== AgentSessionStatus.INPUT_NEEDED)
      ) {
        component = <CustomizeControls />;
       }
    } else if (
      task === AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT &&
      (inProgressStep?.type === AgentSessionStepType.EDIT_RESPONSE ||
        (!inProgressStep &&
          (prepareReportStep?.status === AgentSessionStatus.PENDING ||
            prepareReportStep?.status === AgentSessionStatus.COMPLETE)))
    ) {
      // Vendor assessment tabs
      component = <VendorRiskAssessment />;
    } else if (
      inProgressStep?.type === AgentSessionStepType.CONTROL_SELECTION ||
      inProgressStep?.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING
    ) {
      component = <CustomizeControls />;
    }
    else if (
      inProgressStep?.type === AgentSessionStepType.POLICY_MANAGEMENT ||
      (task === AGENT_TYPES.POLICY_MANAGEMENT && 
       inProgressStep?.type === AgentSessionStepType.EDIT_RESPONSE && 
       (inProgressStep?.status === AgentSessionStatus.INPUT_NEEDED ||
        inProgressStep?.status === AgentSessionStatus.IN_PROGRESS)) ||
      (task === AGENT_TYPES.POLICY_MANAGEMENT && 
       !inProgressStep && 
       prepareReportStep?.status === AgentSessionStatus.PENDING)
    ) {
      const refineStep = stepData.find(
        (step) =>
          step.type === AgentSessionStepType.POLICY_MANAGEMENT
      );
      if(refineStep?.status === AgentSessionStatus.REFINE_IN_PROGRESS){
        component = <Editor isRefine={true} />;
      }
      else{
        component = <Editor isRefine={false} />;
      }
      isIntermediate = false;
    } else if (
      inProgressStep?.type === AgentSessionStepType.EDIT_RESPONSE ||
      (!inProgressStep &&
        prepareReportStep?.status === AgentSessionStatus.PENDING)
    ) {
      component = <ReviewResponse />;
      isIntermediate = false;
    } else if (
      prepareReportStep &&
      prepareReportStep.status === AgentSessionStatus.COMPLETE
    ) {
      component = <ReviewResponse />;
    } else if (
      inProgressStep?.type ===
      AgentSessionStepType.MANUAL_EXCEL_STRUCTURE_MAPPING
    ) {
      component = <QuestionnaireMapping stepsMetaData={inProgressStep} />;
    }
    let breadcrumb: string | undefined;

    if (prepareReportStep?.status === AgentSessionStatus.COMPLETE) {
      breadcrumb = data.process[prepareReportStep?.type]?.breadcrumb ?? "";
    } else if (inProgressStep) {
      breadcrumb = data.process[inProgressStep?.type]?.breadcrumb ?? "";
    }
    // for vendor assessment, the breadcrumb is the vendor name
    if (sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      const baseTitle = helper[agentSubType].title;
      const vendorName = sessionData?.multi_file_vendor_assessment?.vendor_name || "";
      const productName = sessionData?.multi_file_vendor_assessment?.product_name || "";
      
      data.title = `${vendorName} ${productName} ${baseTitle}`.trim();
    }

    if ([GapAssessmentTypes.SOC2_TYPE1, GapAssessmentTypes.SOC2_TYPE2].includes(agentSubType as any) && !isCustomControls) {
      breadcrumb = "Upload Documents"
    }

    // Find the control selection step
    const controlSelectionStep = stepData.find(
      (step) => step.type === AgentSessionStepType.CONTROL_SELECTION
    );

    // Dynamic breadcrumb for CONTROL_SELECTION step based on custom controls usage
    if (controlSelectionStep &&
        sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT &&
        prepareReportStep?.status !== AgentSessionStatus.COMPLETE) {
      breadcrumb = sessionData?.customize ? "Custom Controls" : "Review Responses";
      if(inProgressStep?.status === AgentSessionStatus.IN_PROGRESS){
        breadcrumb = "In Progress"
      }
    }

    if (sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT && inProgressStep?.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
      breadcrumb = "Intake Form"
      const vendorName = sessionData?.multi_file_vendor_assessment?.vendor_name || "";
      const productName = sessionData?.multi_file_vendor_assessment?.product_name || "";
      
      data.title = (`${vendorName} ${productName} `) + "Third-Party Risk Assessment"
    }

    // Dynamic breadcrumb for vendor intake form (Add Details)
    if (inProgressStep?.type === AgentSessionStepType.MULTI_VENDOR_PROFILE && sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      const controlSelection = stepData.find(
        (step) => step.type === AgentSessionStepType.CONTROL_SELECTION
      );

      // Check if we're showing the vendor intake form (not the customize controls or waiting screen)
      if (sessionData?.status !== AgentSessionStatus.FORM_SENT &&
        !(stepData[0]?.status === AgentSessionStatus.VENDOR_INPUT_NEEDED) &&
        !(controlSelection?.status === AgentSessionStatus.INPUT_NEEDED)) {
        breadcrumb = "Add Details";
      }

      if (inProgressStep?.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
        breadcrumb = "Intake Form";
      }
    }

    // Override breadcrumb for vendor waiting screen (after form is sent)
  if (sessionData?.status === AgentSessionStatus.FORM_SENT && 
    sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
  breadcrumb = "Third-Party Due-Diligence";
  }

    if(agentSubType === GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK){
      data.title = (customFrameworksList?.mapping[(sessionData as CustomGapAgentSession)?.gap_custom_framework?.framework_id ?? ""] || "") + " Gap Assessment"
    }

    return {
      title: data.title,
      breadcrumb: breadcrumb,
      currentComponent: component,
      isIntermediate: isIntermediate,
    };
  }, [id, stepData, pathname, customFrameworksList, searchParams]);

  const prepareReportStep = stepData?.find(
    (step) => step.type === AgentSessionStepType.PREPARE_REPORT
  );

  // close ask ai popup on component unmount
  useEffect(() => {
    return () => {
      setIsFloatingAskAiOpen(false);
    };
  }, []);

  // Check if should show reopened session banner
  const { agentType, agentSubType } = getAgentTypeFromId(id);

  // Find prepare_report step to check if session is complete
  const prepareReportStepForBanner = stepData?.find(
    (step) => step.type === AgentSessionStepType.PREPARE_REPORT
  );

  const shouldShowBanner =
    agentType === AGENT_TYPES.RISK_ASSESSMENT &&
    agentSubType === RiskAssessmentTypes.FAIR &&
    sessionData?.reopened_at != null &&
    sessionData?.reopened_by != null &&
    prepareReportStepForBanner?.status !== AgentSessionStatus.COMPLETE;

  // Manage MainLayout padding for full-width banner
  useEffect(() => {
    if (shouldShowBanner) {
      setShouldRemovePadding(true);
    }

    // Cleanup: restore padding when component unmounts or banner hidden
    return () => {
      setShouldRemovePadding(false);
    };
  }, [shouldShowBanner, setShouldRemovePadding]);

  return (
    <div className="w-full h-full flex flex-col">
      {/* Reopened Session Banner - Full Width */}
      {shouldShowBanner && (
        <ReopenedSessionBanner
          sessionId={id}
          reopenedAt={sessionData.reopened_at!}
          reopenedBy={sessionData.reopened_by!}
        />
      )}

      <div className={cn(
        "flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]",
        shouldShowBanner && "px-4 pt-4"
      )}>
        <div className="flex gap-4 items-center">
          <ZBreadCrumbs
            items={
              sessionData?.type === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT
                ? [
                    {
                      title: "Third-Party Risk",
                      href: "/third-party-risk",
                    },
                    {
                      title: sessionData?.multi_file_vendor_assessment?.vendor_name || "",
                      href: "",
                    },
                    {
                      title: sessionData?.multi_file_vendor_assessment?.product_name || "All Products",
                      href: "",
                    },
                    {
                      title: (
                        <div className="flex items-center gap-2">
                          <span>{breadcrumb || workFlowName}</span>
                          {(isViewMode || prepareReportStep?.status === AgentSessionStatus.COMPLETE) && (
                            <div className="flex items-center gap-1 bg-[#000000] rounded-xl px-2 py-[1px] opacity-80">
                              <Eye size={14} className="text-white opacity-80" />
                              <span className="text-[10px] text-white opacity-80">
                                View Only
                              </span>
                            </div>
                          )}
                        </div>
                      ) as ReactNode,
                      href: "",
                    },
                  ]
                : [
                    {
                      title: title,
                      href: ``,
                      description: workFlowName,
                    },
                    {
                      title: (
                        <div className="flex items-center gap-2">
                          <span>{breadcrumb || workFlowName}</span>
                          {prepareReportStep?.status ===
                            AgentSessionStatus.COMPLETE && (
                            <div className="flex items-center gap-1 bg-[#000000] rounded-xl px-2 py-[1px] opacity-80">
                              <Eye size={14} className="text-white opacity-80" />
                              <span className="text-[10px] text-white opacity-80">
                                View Only
                              </span>
                            </div>
                          )}
                        </div>
                      ) as ReactNode,

                      href: ``,
                    },
                  ]
            }
          />
        </div>
        {!isIntermediate ? <AgentHeader /> : null}
      </div>
      <div className={cn(
        "grow overflow-hidden rounded-xl",
        shouldShowBanner && "mx-4"
      )}>
        <Suspense
          fallback={
            <div className="w-full h-full bg-white flex items-center justify-center px-24">
              <Spinner />
            </div>
          }
        >
          {currentComponent}
          {isFloatingAskAiOpen ? <AskAi /> : null}
        </Suspense>
      </div>
      {/* <ImportModal isOpen={true} onClose={() => {}} onImport={() => {}} /> */}
    </div>
  );
};

export default ChatLayout;
